import React, { useState, useEffect, useCallback, useMemo } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { Batiment, Personnel, Vehicule } from '../../../shared/types/game';
import BuildingInfoTab from './BuildingDetailModal/BuildingInfoTab';
import BuildingPersonnelTab from './BuildingDetailModal/BuildingPersonnelTab';
import BuildingVehiculesTab from './BuildingDetailModal/BuildingVehiculesTab';
import BuildingDetailsTab from './BuildingDetailModal/BuildingDetailsTab';
import BuildingDetailsCTATab from './BuildingDetailModal/BuildingDetailsCTATab';
import BuildingCellulesTab from './BuildingDetailModal/BuildingCellulesTab';
import BuildingLitsTab from './BuildingDetailModal/BuildingLitsTab';
import BuildingCTATab from './BuildingDetailModal/BuildingCTATab';
import BuildingGererTab from './BuildingDetailModal/BuildingGererTab';
import { useBuildingCapacity } from './BuildingDetailModal/useBuildingCapacity';
import {
  getBuildingIconPath,
  getBuildingIconConfig,
  fetchBuildingPersonnel,
  fetchBuildingVehicules,
} from './BuildingDetailModal/buildingDetailHelpers';

type TabKey = 'info' | 'personnel' | 'vehicules' | 'cellules' | 'lits' | 'details' | 'cta' | 'gerer';

export interface BuildingDetailModalProps {
  building: Batiment;
  address?: string;
  allBuildings?: Batiment[];
  initialTab?: TabKey;
  onClose: () => void;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
}

const BuildingDetailModal: React.FC<BuildingDetailModalProps> = ({
  building,
  address,
  allBuildings,
  initialTab,
  onClose,
  onBuildingUpdate,
}) => {
  const { t } = usePageTranslation('game', 'Map');
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab ?? 'info');
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [loadingVehicules, setLoadingVehicules] = useState(false);
  const [buildingData, setBuildingData] = useState<Batiment>(building);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(building.nom);

  const ctaName = useMemo(
    () => allBuildings?.find((b) => b.type === 'cta' && b.id === buildingData.cta_id)?.nom,
    [allBuildings, buildingData.cta_id]
  );

  const handleBuildingUpdate = useCallback(
    (updated: Batiment) => {
      setBuildingData(updated);
      onBuildingUpdate?.(updated);
    },
    [onBuildingUpdate]
  );

  const {
    calculatedLevel,
    totalCapacity,
    totalVehiclesCapacity,
    timeRemainingPersonnel,
    timeRemainingVehicules,
    handleStartAugmentation,
  } = useBuildingCapacity(buildingData, setBuildingData, handleBuildingUpdate);

  const handleSaveName = () => {
    handleBuildingUpdate({ ...buildingData, nom: editedName, updated_at: new Date().toISOString() });
    setIsEditingName(false);
  };

  const reloadPersonnel = useCallback(async () => {
    setLoadingPersonnel(true);
    try {
      const list = await fetchBuildingPersonnel(buildingData.id);
      if (list) setPersonnel(list);
    } finally {
      setLoadingPersonnel(false);
    }
  }, [buildingData.id]);

  const reloadVehicules = useCallback(async () => {
    setLoadingVehicules(true);
    try {
      const list = await fetchBuildingVehicules(buildingData.id);
      if (list) setVehicules(list);
    } finally {
      setLoadingVehicules(false);
    }
  }, [buildingData.id]);

  useEffect(() => {
    reloadPersonnel();
    reloadVehicules();
  }, [reloadPersonnel, reloadVehicules]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const serviceIconPath = getBuildingIconPath(buildingData.type);
  const iconConfig = getBuildingIconConfig(buildingData.type);

  const tabs: { key: TabKey; label: string; show: boolean; count: number | null }[] = [
    { key: 'info',      label: 'Informations',    show: true,                                          count: null },
    { key: 'gerer',     label: 'Vue opérationnelle', show: buildingData.type !== 'cta',                count: null },
    { key: 'personnel', label: 'Personnel',        show: buildingData.type !== 'cta',                  count: personnel.length > 0 ? personnel.length : null },
    { key: 'vehicules', label: 'Véhicules',        show: buildingData.type !== 'cta',                  count: vehicules.length > 0 ? vehicules.length : null },
    { key: 'cellules',  label: 'Cellules',         show: buildingData.type === 'prison' || buildingData.type === 'fdo', count: (buildingData.capacite ?? 0) > 0 ? (buildingData.capacite ?? 0) : null },
    { key: 'lits',      label: 'Chambres',         show: buildingData.type === 'hopital',              count: (buildingData.capacite ?? 0) > 0 ? (buildingData.capacite ?? 0) : null },
    { key: 'cta',       label: 'Bâtiments liés',   show: buildingData.type === 'cta',                  count: null },
    { key: 'details',   label: 'Détails opé.',     show: true,                                          count: null },
  ];

  const isGererLikeTab = activeTab === 'gerer';

  return (
    <div
      className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-zinc-950 rounded-xl shadow-2xl w-full border border-zinc-800/90 flex flex-col ${
          isGererLikeTab ? 'max-w-6xl max-h-[92vh]' : 'max-w-5xl max-h-[88vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center gap-4 px-5 py-4 border-b border-zinc-800/80 bg-zinc-900/60">
          <div
            className="rounded-lg flex items-center justify-center border border-white/10 shadow-lg shrink-0"
            style={{ backgroundColor: iconConfig.backgroundColor, width: '42px', height: '42px' }}
          >
            <img src={serviceIconPath} alt={buildingData.type} className="w-7 h-7" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-white truncate">{buildingData.nom}</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t(`contextMenu.buildingTypes.${buildingData.type}`)}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
              buildingData.actif
                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                : 'bg-zinc-700/50 text-zinc-500 border-zinc-600/30'
            }`}>
              {buildingData.actif ? 'Actif' : 'Inactif'}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-sky-500/15 text-sky-400 border border-sky-500/30">
              Niv. {buildingData.niveau}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-lg p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors ml-1"
              aria-label="Fermer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="shrink-0 flex overflow-x-auto border-b border-zinc-800/80 bg-zinc-900/40 scrollbar-none">
          {tabs.filter((tab) => tab.show).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap px-4 py-3 text-xs font-semibold transition-colors border-b-2 ${
                activeTab === tab.key
                  ? 'border-sky-500 text-sky-300 bg-zinc-800/40'
                  : 'border-transparent text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/20'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
                  activeTab === tab.key ? 'bg-sky-500/20 text-sky-300' : 'bg-zinc-700/80 text-zinc-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className={`flex-1 min-h-0 ${isGererLikeTab ? 'overflow-hidden p-4' : 'overflow-y-auto p-5'} bg-zinc-950/50`}>

          {activeTab === 'info' && (
            <BuildingInfoTab
              building={buildingData}
              address={address}
              ctaName={ctaName}
              calculatedLevel={calculatedLevel}
              totalCapacity={totalCapacity}
              totalVehiclesCapacity={totalVehiclesCapacity}
              timeRemainingPersonnel={timeRemainingPersonnel}
              timeRemainingVehicules={timeRemainingVehicules}
              handleStartAugmentation={handleStartAugmentation}
              onBuildingUpdate={handleBuildingUpdate}
              isEditingName={isEditingName}
              editedName={editedName}
              onStartEditName={() => setIsEditingName(true)}
              onChangeEditedName={setEditedName}
              onSaveName={handleSaveName}
              onCancelEditName={() => { setEditedName(buildingData.nom); setIsEditingName(false); }}
            />
          )}

          {activeTab === 'gerer' && buildingData.type !== 'cta' && (
            <BuildingGererTab
              building={buildingData}
              personnel={personnel}
              vehicules={vehicules}
              loadingPersonnel={loadingPersonnel}
              loadingVehicules={loadingVehicules}
              onPersonnelAdded={reloadPersonnel}
              onVehiculeAdded={reloadVehicules}
            />
          )}

          {activeTab === 'personnel' && buildingData.type !== 'cta' && (
            <BuildingPersonnelTab
              personnel={personnel}
              loadingPersonnel={loadingPersonnel}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              buildingName={buildingData.nom}
              userId={buildingData.user_id}
              onPersonnelAdded={reloadPersonnel}
            />
          )}

          {activeTab === 'vehicules' && buildingData.type !== 'cta' && (
            <BuildingVehiculesTab
              vehicules={vehicules}
              loadingVehicules={loadingVehicules}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              userId={buildingData.user_id}
              onVehiculeAdded={reloadVehicules}
            />
          )}

          {activeTab === 'cellules' && (
            <BuildingCellulesTab
              building={buildingData}
              onIncreaseCapacity={buildingData.type === 'prison' ? () => handleStartAugmentation('personnel', 1) : undefined}
            />
          )}

          {activeTab === 'lits' && (
            <BuildingLitsTab building={buildingData} />
          )}

          {activeTab === 'cta' && buildingData.type === 'cta' && (
            <BuildingCTATab ctaBuilding={buildingData} onBuildingUpdate={handleBuildingUpdate} />
          )}

          {activeTab === 'details' && buildingData.type === 'cta' && (
            <BuildingDetailsCTATab ctaBuilding={buildingData} />
          )}

          {activeTab === 'details' && buildingData.type !== 'cta' && (
            <BuildingDetailsTab
              building={buildingData}
              personnel={personnel}
              vehicules={vehicules}
              calculatedLevel={calculatedLevel}
              totalCapacity={totalCapacity}
              totalVehiclesCapacity={totalVehiclesCapacity}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default BuildingDetailModal;
